import React, { useState, useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { serverLink } from "../../../resources/url";
import DataLoader from "../../common/dataLoader/dataLoader";
import ApexChart from "react-apexcharts";
import { useReactToPrint } from "react-to-print";

// ─── Palette & base config ────────────────────────────────────────────────────

const C = {
    primary: '#206bc4', success: '#2fb344', warning: '#f76707',
    danger:  '#d63939', info:    '#4299e1', purple:  '#ae3ec9',
    palette: ['#206bc4','#2fb344','#f76707','#ae3ec9','#d63939','#4299e1','#f59f00','#6c757d','#20c997','#fd7e14'],
};

const BASE = { toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: true, speed: 600 } };

// ─── Utilities ────────────────────────────────────────────────────────────────

const fmtCurrency = (n) =>
    `₦${Number(n).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtShort = (n) => {
    if (n >= 1_000_000_000) return `₦${(n / 1e9).toFixed(2)}B`;
    if (n >= 1_000_000)     return `₦${(n / 1e6).toFixed(2)}M`;
    if (n >= 1_000)         return `₦${(n / 1e3).toFixed(1)}K`;
    return `₦${Number(n).toFixed(2)}`;
};

const parseAmount = (val) => {
    const n = parseFloat(String(val).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
};

const parseDate = (dp) => {
    if (!dp || !String(dp).trim()) return null;
    const d = new Date(dp);
    if (!isNaN(d.getTime())) return d;
    const pts = String(dp).split('/');
    if (pts.length === 3) {
        const d2 = new Date(`${pts[2]}-${pts[1].padStart(2, '0')}-${pts[0].padStart(2, '0')}`);
        if (!isNaN(d2.getTime())) return d2;
    }
    return null;
};

const parseYear = (dp) => { const d = parseDate(dp); return d ? d.getFullYear() : null; };
const pct = (n, d) => (d === 0 ? '0%' : `${((n / d) * 100).toFixed(1)}%`);

const TABS = [
    { key: 'year', label: 'By Year'         },
    { key: 'type', label: 'By Payment Type' },
];

// ─── KPI Icons ────────────────────────────────────────────────────────────────

function KpiIcon({ type, color }) {
    const s = {
        width: 18, height: 18, viewBox: '0 0 24 24', strokeWidth: 2,
        stroke: color, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round',
    };
    const icons = {
        txn:    <><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></>,
        amount: <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
        avg:    <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
        users:  <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
        star:   <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    };
    return <svg xmlns="http://www.w3.org/2000/svg" {...s}>{icons[type]}</svg>;
}

// ─── Charts ───────────────────────────────────────────────────────────────────

function CombinedYearChart({ breakdown, height = 300 }) {
    const cats   = breakdown.map(([k]) => k);
    const series = [
        { name: 'Transactions', type: 'column', data: breakdown.map(([, v]) => v.count)  },
        { name: 'Amount (₦)',   type: 'line',   data: breakdown.map(([, v]) => v.amount) },
    ];
    return (
        <ApexChart type="line" height={height} series={series} options={{
            chart: { ...BASE, type: 'line' },
            stroke: { width: [0, 3], curve: 'smooth' },
            fill: { opacity: [0.85, 1] },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
            dataLabels: { enabled: false },
            xaxis: { categories: cats, labels: { style: { fontSize: '11px' } } },
            yaxis: [
                {
                    title: { text: 'Transactions', style: { color: C.primary } },
                    labels: { formatter: v => Math.round(v).toLocaleString(), style: { colors: [C.primary] } },
                },
                {
                    opposite: true,
                    title: { text: 'Amount (₦)', style: { color: C.success } },
                    labels: { formatter: v => fmtShort(v), style: { colors: [C.success] } },
                },
            ],
            colors: [C.primary, C.success],
            legend: { position: 'top', fontSize: '12px' },
            tooltip: {
                shared: true, intersect: false,
                y: [
                    { formatter: v => `${v?.toLocaleString()} txns` },
                    { formatter: v => fmtCurrency(v ?? 0) },
                ],
            },
            grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
        }} />
    );
}

function YearAreaChart({ breakdown, height = 260 }) {
    const cats = breakdown.map(([k]) => k);
    return (
        <ApexChart type="area" height={height}
            series={[{ name: 'Transactions', data: breakdown.map(([, v]) => v.count) }]}
            options={{
                chart: { ...BASE, type: 'area' },
                stroke: { curve: 'smooth', width: 2.5 },
                fill: { type: 'gradient', gradient: { opacityFrom: 0.40, opacityTo: 0.03 } },
                dataLabels: { enabled: false },
                xaxis: { categories: cats, labels: { style: { fontSize: '11px' } } },
                yaxis: { labels: { formatter: v => Math.round(v).toLocaleString() } },
                colors: [C.primary],
                markers: { size: 4, strokeWidth: 0, hover: { size: 6 } },
                tooltip: { y: { formatter: v => `${v.toLocaleString()} transactions` } },
                grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
            }} />
    );
}

function YearAmountBarChart({ breakdown, height = 260 }) {
    const cats = breakdown.map(([k]) => k);
    return (
        <ApexChart type="bar" height={height}
            series={[{ name: 'Total Amount (₦)', data: breakdown.map(([, v]) => v.amount) }]}
            options={{
                chart: { ...BASE, type: 'bar' },
                plotOptions: { bar: { borderRadius: 4, columnWidth: '52%', distributed: true } },
                dataLabels: { enabled: false },
                xaxis: { categories: cats, labels: { style: { fontSize: '11px' } } },
                yaxis: { labels: { formatter: v => fmtShort(v) } },
                colors: C.palette,
                legend: { show: false },
                tooltip: { y: { formatter: v => fmtCurrency(v) } },
                grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
            }} />
    );
}

function PayTypeDonutChart({ breakdown, height = 300 }) {
    const labels = breakdown.map(([k]) => k || 'N/A');
    const values = breakdown.map(([, v]) => v.count);
    const total  = values.reduce((a, b) => a + b, 0);
    return (
        <ApexChart type="donut" height={height} series={values} options={{
            chart: { ...BASE, type: 'donut' },
            labels,
            colors: C.palette,
            legend: { position: 'bottom', fontSize: '12px' },
            dataLabels: { formatter: val => `${val.toFixed(1)}%` },
            plotOptions: {
                pie: {
                    donut: {
                        size: '64%',
                        labels: {
                            show: true,
                            total: {
                                show: true, label: 'Transactions', fontSize: '12px', fontWeight: 600,
                                formatter: () => total.toLocaleString(),
                            },
                            value: { fontSize: '20px', fontWeight: 700, formatter: v => Number(v).toLocaleString() },
                        },
                    },
                },
            },
            tooltip: { y: { formatter: v => `${v.toLocaleString()} transactions` } },
        }} />
    );
}

function PayTypeAmountBarChart({ breakdown, height = 300 }) {
    const sorted = [...breakdown].sort((a, b) => b[1].amount - a[1].amount);
    const h      = Math.max(height, sorted.length * 52 + 80);
    return (
        <ApexChart type="bar" height={h}
            series={[{ name: 'Amount (₦)', data: sorted.map(([, v]) => v.amount) }]}
            options={{
                chart: { ...BASE, type: 'bar' },
                plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '60%', distributed: true } },
                dataLabels: {
                    enabled: true, offsetX: 6,
                    style: { fontSize: '10px', colors: ['#354052'] },
                    formatter: v => fmtShort(v),
                },
                xaxis: {
                    categories: sorted.map(([k]) => k || 'N/A'),
                    labels: { formatter: v => fmtShort(v), style: { fontSize: '10px' } },
                },
                yaxis: { labels: { style: { fontSize: '10px' }, maxWidth: 200 } },
                colors: C.palette,
                legend: { show: false },
                tooltip: { y: { formatter: v => fmtCurrency(v) } },
                grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
            }} />
    );
}

// ─── Breakdown Table ──────────────────────────────────────────────────────────

function PayBreakdownTable({ rows, totalTxns }) {
    const grandAmt = rows.reduce((s, r) => s + r.amount, 0);
    const grandCnt = rows.reduce((s, r) => s + r.count, 0);
    return (
        <div className="table-responsive">
            <table className="table table-vcenter table-striped table-sm mb-0">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>Group</th>
                        <th className="text-end">Transactions</th>
                        <th className="text-end">Total Amount</th>
                        <th className="text-end">Avg. Amount</th>
                        <th className="text-end">Share</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0
                        ? <tr><td colSpan={6} className="text-center text-muted py-4">No data available</td></tr>
                        : rows.map((r, i) => (
                            <tr key={i}>
                                <td className="text-muted">{i + 1}</td>
                                <td><strong>{r.label}</strong></td>
                                <td className="text-end">{r.count.toLocaleString()}</td>
                                <td className="text-end fw-bold" style={{ color: C.success }}>{fmtCurrency(r.amount)}</td>
                                <td className="text-end">{fmtCurrency(r.count > 0 ? r.amount / r.count : 0)}</td>
                                <td className="text-end">
                                    <span className="badge" style={{ background: `${C.primary}18`, color: C.primary, fontSize: '11px' }}>
                                        {pct(r.count, totalTxns)}
                                    </span>
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
                {rows.length > 0 && (
                    <tfoot className="table-light fw-bold">
                        <tr>
                            <td colSpan={2}>Total</td>
                            <td className="text-end">{grandCnt.toLocaleString()}</td>
                            <td className="text-end" style={{ color: C.success }}>{fmtCurrency(grandAmt)}</td>
                            <td className="text-end">{fmtCurrency(grandCnt > 0 ? grandAmt / grandCnt : 0)}</td>
                            <td className="text-end">100%</td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

// ─── Chart card wrapper ───────────────────────────────────────────────────────

function CC({ title, subtitle, col, children }) {
    return (
        <div className={col}>
            <div className="card h-100">
                <div className="card-header">
                    <div>
                        <h3 className="card-title mb-0">{title}</h3>
                        {subtitle && <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.75rem' }}>{subtitle}</p>}
                    </div>
                </div>
                <div className="card-body">{children}</div>
            </div>
        </div>
    );
}

// ─── Printable Report ─────────────────────────────────────────────────────────

const PrintableReport = React.forwardRef(({ kpis, yearBD, typeBD, filters }, ref) => {
    const { totalTxns, totalAmount, avgAmount, uniquePayers, topType } = kpis;
    const date = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

    const activeFilters = [];
    if (filters.filterYear    !== 'All') activeFilters.push(`Year: ${filters.filterYear}`);
    if (filters.filterPayType !== 'All') activeFilters.push(`Type: ${filters.filterPayType}`);
    if (filters.filterDateFrom) activeFilters.push(`From: ${filters.filterDateFrom}`);
    if (filters.filterDateTo)   activeFilters.push(`To: ${filters.filterDateTo}`);

    const toRows = (bd) => bd.map(([k, v]) => ({ label: k || 'N/A', count: v.count, amount: v.amount }));
    const yearRows = toRows(yearBD);
    const typeRows = toRows(typeBD);

    const SectionTable = ({ title, rows }) => {
        const grandCnt = rows.reduce((s, r) => s + r.count, 0);
        const grandAmt = rows.reduce((s, r) => s + r.amount, 0);
        return (
            <div style={{ marginBottom: 16 }}>
                <h2>{title}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>#</th><th>Group</th><th>Transactions</th>
                            <th>Total Amount</th><th>Avg Amount</th><th>Share</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{r.label}</td>
                                <td>{r.count.toLocaleString()}</td>
                                <td>{fmtCurrency(r.amount)}</td>
                                <td>{fmtCurrency(r.count > 0 ? r.amount / r.count : 0)}</td>
                                <td>{pct(r.count, totalTxns)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={2}>Total</td>
                            <td>{grandCnt.toLocaleString()}</td>
                            <td>{fmtCurrency(grandAmt)}</td>
                            <td>{fmtCurrency(grandCnt > 0 ? grandAmt / grandCnt : 0)}</td>
                            <td>100%</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };

    return (
        <div ref={ref} style={{ padding: '6mm', fontFamily: 'Arial, sans-serif', width: '100%' }}>
            <div className="report-header-bar" style={{ textAlign: 'center' }}>
                <h1 style={{ margin: 0, color: '#fff' }}>ODFeL — Kaduna Polytechnic</h1>
                <p style={{ margin: '4px 0 0', color: '#e0ecff', fontSize: '12px' }}>
                    Payment Executive Dashboard &nbsp;|&nbsp; Generated: {date}
                    {activeFilters.length > 0 && <> &nbsp;|&nbsp; Filters: {activeFilters.join(', ')}</>}
                </p>
            </div>

            <div className="kpi-row">
                {[
                    ['Total Transactions', totalTxns.toLocaleString(),    null],
                    ['Total Amount',       fmtShort(totalAmount),         fmtCurrency(totalAmount)],
                    ['Avg. Per Txn',       fmtShort(avgAmount),           fmtCurrency(avgAmount)],
                    ['Unique Payers',      uniquePayers.toLocaleString(), null],
                    ['Top Payment Type',   topType,                       null],
                ].map(([lbl, val, sub]) => (
                    <div key={lbl} className="kpi-box">
                        <div className="kpi-lbl">{lbl}</div>
                        <div className="kpi-val">{val}</div>
                        {sub && <div className="kpi-sub">{sub}</div>}
                    </div>
                ))}
            </div>

            <SectionTable title="Breakdown by Year (Date Paid)" rows={yearRows} />
            <SectionTable title="Breakdown by Payment Type"     rows={typeRows} />

            <h2 style={{ marginBottom: 12 }}>Chart Trends</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div className="chart-card">
                    <div className="chart-card-title">Year-over-Year Payment Trend</div>
                    <CombinedYearChart breakdown={yearBD} height={220} />
                </div>
                <div className="chart-card">
                    <div className="chart-card-title">Payment Type Distribution</div>
                    <PayTypeDonutChart breakdown={typeBD} height={220} />
                </div>
            </div>
            <div className="chart-card">
                <div className="chart-card-title">Revenue by Payment Type</div>
                <PayTypeAmountBarChart breakdown={typeBD} />
            </div>
        </div>
    );
});
PrintableReport.displayName = 'PrintableReport';

// ─── Main Component ───────────────────────────────────────────────────────────

function PaymentExecutiveDashboard() {
    const [isLoading,      setIsLoading]      = useState(true);
    const [payments,       setPayments]       = useState([]);
    const [filterYear,     setFilterYear]     = useState('All');
    const [filterPayType,  setFilterPayType]  = useState('All');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo,   setFilterDateTo]   = useState('');
    const [activeTab,      setActiveTab]      = useState('year');
    const printRef = useRef(null);

    useEffect(() => { getData(); }, []);

    const getData = async () => {
        try {
            const result = await axios.get(`${serverLink}staff/report/payment/analytics`);
            setPayments(result.data || []);
        } catch (e) {
            console.log("NETWORK ERROR");
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Derived filter options ──────────────────────────────────────────────

    const allYears = useMemo(() => {
        const s = new Set(payments.map(p => parseYear(p.DATE_PAID)).filter(Boolean));
        return [...s].sort((a, b) => a - b).map(String);
    }, [payments]);

    const allPayTypes = useMemo(() => {
        const s = new Set(payments.map(p => p.PAYMENT_TYPE).filter(Boolean));
        return [...s].sort();
    }, [payments]);

    // ─── Filtered records ────────────────────────────────────────────────────

    const filtered = useMemo(() => payments.filter(p => {
        const d  = parseDate(p.DATE_PAID);
        const yr = d ? String(d.getFullYear()) : null;
        if (filterYear    !== 'All' && yr           !== filterYear)    return false;
        if (filterPayType !== 'All' && p.PAYMENT_TYPE !== filterPayType) return false;
        if (filterDateFrom && d && d < new Date(filterDateFrom))       return false;
        if (filterDateTo   && d && d > new Date(filterDateTo + 'T23:59:59')) return false;
        return true;
    }), [payments, filterYear, filterPayType, filterDateFrom, filterDateTo]);

    // ─── KPI metrics ─────────────────────────────────────────────────────────

    const totalTxns    = filtered.length;
    const totalAmount  = useMemo(() => filtered.reduce((s, p) => s + parseAmount(p.AMOUNT), 0), [filtered]);
    const avgAmount    = totalTxns > 0 ? totalAmount / totalTxns : 0;
    const uniquePayers = useMemo(() =>
        new Set(filtered.map(p => p.STUDENTID || p.APPID || p.EMAIL).filter(Boolean)).size, [filtered]);
    const topType = useMemo(() => {
        const cnt = {};
        filtered.forEach(p => { cnt[p.PAYMENT_TYPE] = (cnt[p.PAYMENT_TYPE] || 0) + 1; });
        const s = Object.entries(cnt).sort((a, b) => b[1] - a[1]);
        return s.length ? s[0][0] : '—';
    }, [filtered]);

    // ─── Breakdowns ──────────────────────────────────────────────────────────

    const yearBD = useMemo(() => {
        const g = {};
        filtered.forEach(p => {
            const k = String(parseYear(p.DATE_PAID) ?? 'N/A');
            if (!g[k]) g[k] = { count: 0, amount: 0 };
            g[k].count++;
            g[k].amount += parseAmount(p.AMOUNT);
        });
        return Object.entries(g).sort((a, b) => Number(a[0]) - Number(b[0]));
    }, [filtered]);

    const typeBD = useMemo(() => {
        const g = {};
        filtered.forEach(p => {
            const k = p.PAYMENT_TYPE || 'N/A';
            if (!g[k]) g[k] = { count: 0, amount: 0 };
            g[k].count++;
            g[k].amount += parseAmount(p.AMOUNT);
        });
        return Object.entries(g).sort((a, b) => b[1].count - a[1].count);
    }, [filtered]);

    const yearRows = yearBD.map(([k, v]) => ({ label: k, ...v }));
    const typeRows = typeBD.map(([k, v]) => ({ label: k, ...v }));
    const hasFilter = filterYear !== 'All' || filterPayType !== 'All' || filterDateFrom || filterDateTo;

    // ─── Print ───────────────────────────────────────────────────────────────

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Payment-Analytics-${new Date().toISOString().split('T')[0]}`,
        pageStyle: `
            @page { size: A4 landscape; margin: 10mm; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
            body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #1a1a2e; }
            h1  { font-size: 20px; margin: 0 0 4px; }
            h2  { font-size: 14px; margin: 18px 0 7px; color: #206bc4; border-bottom: 2px solid #206bc4; padding-bottom: 4px; }
            p   { font-size: 11px; margin: 0 0 10px; color: #555; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
            thead th { background:#206bc4 !important; color:#fff !important; padding:5px 7px; text-align:left; font-size:11px; white-space:nowrap; }
            tbody td { padding:4px 7px; border-bottom:1px solid #e9ecef; font-size:11px; }
            tbody tr:nth-child(even) td { background:#f0f4ff !important; }
            tfoot td { padding:4px 7px; font-weight:700; background:#dce7ff !important; font-size:11px; }
            .kpi-row { display:flex; gap:10px; margin-bottom:14px; }
            .kpi-box { flex:1; border:1px solid #206bc4; border-radius:6px; padding:10px; text-align:center; background:#f0f4ff !important; }
            .kpi-val { font-size:18px; font-weight:700; color:#206bc4 !important; }
            .kpi-lbl { font-size:10px; text-transform:uppercase; color:#555; letter-spacing:.05em; font-weight:600; }
            .kpi-sub { font-size:10px; color:#444; margin-top:2px; }
            .chart-card { border:1px solid #dce7ff; border-radius:6px; padding:10px; margin-bottom:14px; background:#f8faff !important; }
            .chart-card-title { font-size:13px; font-weight:700; color:#206bc4; margin-bottom:6px; }
            .report-header-bar { background:#206bc4 !important; color:#fff !important; padding:12px 16px; border-radius:6px; margin-bottom:14px; }
        `,
    });

    // ─── Export Excel ─────────────────────────────────────────────────────────

    const exportExcel = () => {
        const date = new Date().toLocaleDateString('en-GB');
        const thS  = 'background:#206bc4;color:#fff;padding:5px 8px;font-weight:bold;border:1px solid #1a56a0;';
        const tdS  = 'padding:4px 8px;border:1px solid #dee2e6;';
        const tdAS = 'padding:4px 8px;border:1px solid #dee2e6;background:#f8f9fc;';
        const hdS  = 'font-size:13px;font-weight:bold;color:#206bc4;padding:8px 4px;';

        const mkTbl = (title, rows) => {
            const headers = ['Group', 'Transactions', 'Total Amount (₦)', 'Avg Amount (₦)', 'Share'];
            const head    = `<tr>${headers.map(h => `<th style="${thS}">${h}</th>`).join('')}</tr>`;
            const body    = rows.map((r, i) => `<tr>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${r.label}</td>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${r.count.toLocaleString()}</td>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${fmtCurrency(r.amount)}</td>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${fmtCurrency(r.count > 0 ? r.amount / r.count : 0)}</td>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${pct(r.count, totalTxns)}</td>
            </tr>`).join('');
            const totCnt = rows.reduce((s, r) => s + r.count, 0);
            const totAmt = rows.reduce((s, r) => s + r.amount, 0);
            const foot   = `<tr>
                <td style="font-weight:bold;${tdS}">Total</td>
                <td style="font-weight:bold;${tdS}">${totCnt.toLocaleString()}</td>
                <td style="font-weight:bold;${tdS}">${fmtCurrency(totAmt)}</td>
                <td style="font-weight:bold;${tdS}">${fmtCurrency(totCnt > 0 ? totAmt / totCnt : 0)}</td>
                <td style="font-weight:bold;${tdS}">100%</td>
            </tr>`;
            return `
                <tr><td colspan="5" style="${hdS}">${title}</td></tr>
                <table border="0" style="width:100%;border-collapse:collapse;">
                    <thead>${head}</thead><tbody>${body}</tbody><tfoot>${foot}</tfoot>
                </table>
                <tr><td>&nbsp;</td></tr>`;
        };

        const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="utf-8"/></head>
        <body>
        <table style="font-family:Arial;font-size:10px;width:100%;">
            <tr><td colspan="5" style="font-size:16px;font-weight:bold;padding:6px 4px;">ODFeL — Payment Executive Dashboard</td></tr>
            <tr><td colspan="5" style="color:#555;padding:2px 4px;">Generated: ${date} | Transactions: ${totalTxns.toLocaleString()} | Total: ${fmtCurrency(totalAmount)}</td></tr>
            <tr><td>&nbsp;</td></tr>
            ${mkTbl('BREAKDOWN BY YEAR (DATE PAID)', yearRows)}
            ${mkTbl('BREAKDOWN BY PAYMENT TYPE',     typeRows)}
        </table>
        </body></html>`;

        const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `Payment-Analytics-${new Date().toISOString().split('T')[0]}.xls`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="container-xl" style={{ height: '700px' }}>
                <div className="page-header d-print-none"><DataLoader /></div>
            </div>
        );
    }

    return (
        <>
            {/* ── Page Header ─────────────────────────────────────────────── */}
            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Finance / Reports</div>
                            <h2 className="page-title">Payment Executive Dashboard</h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                            <div className="btn-list">
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="18" height="18"
                                        viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2"/>
                                        <path d="M17 9V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4"/>
                                        <rect x="7" y="13" width="10" height="8" rx="2"/>
                                    </svg>
                                    Print Report
                                </button>
                                <button className="btn btn-success" onClick={exportExcel}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="18" height="18"
                                        viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
                                        <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/>
                                        <path d="M8 11h8"/><path d="M8 15h5"/><path d="M11 19v-8"/>
                                    </svg>
                                    Export Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">

                    {/* ── Filter Bar ─────────────────────────────────────────── */}
                    <div className="card mb-3" style={{ border: '1.5px solid #d0e2f7', background: '#f8fbff' }}>
                        <div className="card-body py-3">
                            <div className="row g-2 align-items-end">

                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Year
                                    </label>
                                    <select className="form-select form-select-sm" value={filterYear}
                                        onChange={e => setFilterYear(e.target.value)} style={{ minWidth: 120 }}>
                                        <option value="All">All Years</option>
                                        {allYears.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                </div>

                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Payment Type
                                    </label>
                                    <select className="form-select form-select-sm" value={filterPayType}
                                        onChange={e => setFilterPayType(e.target.value)} style={{ minWidth: 190 }}>
                                        <option value="All">All Types</option>
                                        {allPayTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>

                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Date From
                                    </label>
                                    <input type="date" className="form-control form-control-sm"
                                        value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
                                </div>

                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Date To
                                    </label>
                                    <input type="date" className="form-control form-control-sm"
                                        value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
                                </div>

                                {hasFilter && (
                                    <div className="col-auto">
                                        <button className="btn btn-sm btn-outline-danger"
                                            onClick={() => {
                                                setFilterYear('All');
                                                setFilterPayType('All');
                                                setFilterDateFrom('');
                                                setFilterDateTo('');
                                            }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                                viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                                strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                            </svg>
                                            Clear Filters
                                        </button>
                                    </div>
                                )}

                                <div className="col ms-auto text-end">
                                    <span className="text-muted" style={{ fontSize: '0.76rem' }}>
                                        Showing <strong>{totalTxns.toLocaleString()}</strong> of{' '}
                                        <strong>{payments.length.toLocaleString()}</strong> transactions
                                        {hasFilter && (
                                            <span className="ms-2 badge"
                                                style={{ background: `${C.primary}18`, color: C.primary, fontSize: '10px' }}>
                                                Filtered
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── KPI Cards ──────────────────────────────────────────── */}
                    <div className="row row-deck row-cards mb-3">
                        {[
                            { label: 'Total Transactions',   val: totalTxns.toLocaleString(),    sub: null,                     icon: 'txn',    accent: C.primary },
                            { label: 'Total Amount',         val: fmtShort(totalAmount),         sub: fmtCurrency(totalAmount), icon: 'amount', accent: C.success },
                            { label: 'Avg. Per Transaction', val: fmtShort(avgAmount),           sub: fmtCurrency(avgAmount),   icon: 'avg',    accent: C.info    },
                            { label: 'Unique Payers',        val: uniquePayers.toLocaleString(), sub: null,                     icon: 'users',  accent: C.purple  },
                            { label: 'Top Payment Type',     val: topType,                       sub: null,                     icon: 'star',   accent: C.warning },
                        ].map(({ label, val, sub, icon, accent }) => (
                            <div key={label} className="col-6 col-lg">
                                <div className="card" style={{ borderTop: `3px solid ${accent}` }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <span style={{
                                                width: 34, height: 34, borderRadius: 9,
                                                background: `${accent}18`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                <KpiIcon type={icon} color={accent} />
                                            </span>
                                            <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b', lineHeight: 1.3 }}>
                                                {label}
                                            </span>
                                        </div>
                                        <div className="fw-bold" style={{ fontSize: '1.45rem', wordBreak: 'break-word', color: accent }}>{val}</div>
                                        {sub && <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>{sub}</div>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Summary Banner ─────────────────────────────────────── */}
                    <div className="alert alert-info d-flex align-items-center mb-3" role="alert">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                            strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round"
                            strokeLinejoin="round" className="me-2" style={{ flexShrink: 0 }}>
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <circle cx="12" cy="12" r="9"/>
                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                            <polyline points="11 12 12 12 12 16 13 16"/>
                        </svg>
                        <div style={{ fontSize: '0.85rem' }}>
                            Verified online payments with reference numbers only &mdash;{' '}
                            <strong>{typeBD.length}</strong> payment type{typeBD.length !== 1 ? 's' : ''} across{' '}
                            <strong>{yearBD.length}</strong> year{yearBD.length !== 1 ? 's' : ''}.{' '}
                            Total collected: <strong>{fmtCurrency(totalAmount)}</strong>.
                            {hasFilter && (
                                <span className="ms-2 badge bg-warning text-dark">Filtered View Active</span>
                            )}
                        </div>
                    </div>

                    {/* ── Table Tabs ─────────────────────────────────────────── */}
                    <div style={{ background: '#f0f4fd', borderRadius: '14px', padding: '7px', marginBottom: '16px', overflowX: 'auto' }}>
                        <div style={{ display: 'flex', gap: '4px', minWidth: 'max-content' }}>
                            {TABS.map(tab => {
                                const active = activeTab === tab.key;
                                const count  = tab.key === 'year' ? yearBD.length : typeBD.length;
                                return (
                                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '7px',
                                            padding: '9px 15px', border: 'none', borderRadius: '10px',
                                            cursor: 'pointer', fontSize: '13px',
                                            fontWeight: active ? 600 : 400,
                                            background: active ? C.primary : 'transparent',
                                            color: active ? '#fff' : '#64748b',
                                            transition: 'background 0.15s, color 0.15s',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {tab.label}
                                        <span style={{
                                            fontSize: '10px', fontWeight: 700,
                                            padding: '1px 7px', borderRadius: '20px',
                                            background: active ? 'rgba(255,255,255,0.22)' : '#dde3f0',
                                            color: active ? '#fff' : '#64748b',
                                            minWidth: '20px', textAlign: 'center',
                                        }}>
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-header" style={{ borderBottom: '2px solid #e9ecef', paddingBottom: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ width: '4px', height: '22px', background: C.primary, borderRadius: '3px', flexShrink: 0 }} />
                                <div>
                                    <h3 className="card-title mb-0">
                                        {activeTab === 'year'
                                            ? 'Payment Breakdown by Year (Date Paid)'
                                            : 'Payment Breakdown by Payment Type'}
                                    </h3>
                                    <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.76rem' }}>
                                        {(activeTab === 'year' ? yearBD : typeBD).length} group{(activeTab === 'year' ? yearBD : typeBD).length !== 1 ? 's' : ''}{' '}
                                        &nbsp;·&nbsp; Online Verified Payments
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            <PayBreakdownTable
                                rows={activeTab === 'year' ? yearRows : typeRows}
                                totalTxns={totalTxns}
                            />
                        </div>
                    </div>

                    {/* ── Charts ─────────────────────────────────────────────── */}
                    <div className="mb-2">
                        <h3 className="mb-1" style={{ fontWeight: 600, color: '#354052' }}>Chart Analysis</h3>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                            Visual breakdown of online payment data across all dimensions
                        </p>
                    </div>

                    <div className="row row-deck row-cards">
                        <CC col="col-12"
                            title="Year-over-Year Payment Trend"
                            subtitle="Transaction count and total amount collected per year (online payments with valid reference numbers)">
                            <CombinedYearChart breakdown={yearBD} height={300} />
                        </CC>
                    </div>

                    <div className="row row-deck row-cards mt-0">
                        <CC col="col-lg-5"
                            title="Payment Type Distribution"
                            subtitle="Share of verified online transactions by payment category">
                            <PayTypeDonutChart breakdown={typeBD} height={320} />
                        </CC>
                        <CC col="col-lg-7"
                            title="Revenue by Payment Type"
                            subtitle="Total amount collected per payment category">
                            <PayTypeAmountBarChart breakdown={typeBD} height={320} />
                        </CC>
                    </div>

                    <div className="row row-deck row-cards mt-0">
                        <CC col="col-lg-6"
                            title="Transactions per Year"
                            subtitle="Number of verified online payments by year">
                            <YearAreaChart breakdown={yearBD} height={260} />
                        </CC>
                        <CC col="col-lg-6"
                            title="Revenue per Year"
                            subtitle="Total naira value collected per year">
                            <YearAmountBarChart breakdown={yearBD} height={260} />
                        </CC>
                    </div>

                </div>
            </div>

            {/* ── Hidden printable report ─────────────────────────────────── */}
            <div style={{ position: 'absolute', top: '-9999px', left: 0, width: '1200px', visibility: 'hidden' }}>
                <PrintableReport
                    ref={printRef}
                    kpis={{ totalTxns, totalAmount, avgAmount, uniquePayers, topType }}
                    yearBD={yearBD}
                    typeBD={typeBD}
                    filters={{ filterYear, filterPayType, filterDateFrom, filterDateTo }}
                />
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({ loginData: state.loginDetails });
export default connect(mapStateToProps, null)(PaymentExecutiveDashboard);
